import { createContext, type ReactNode, use, useCallback, useLayoutEffect, useMemo } from "react";
import { useNavigate } from "react-router";

import { setRequestContext } from "~/api/requestContextStore";
import { paths } from "~/config/routes";
import { useAuthSession } from "~/features/auth/hooks/useAuthSession";
import { type UserProfile } from "~/features/auth/services/keycloakService";
import { type OrganizationInfo } from "~/features/auth/types/keycloak";

interface AuthContextType {
  user: UserProfile | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoggingOut: boolean;
  tenantId: string | null;
  tokenStatus: "valid" | "expiring" | "expired" | "unknown";
  selectedTenantId: string | null;
  availableOrganizations: OrganizationInfo[];
  /** True if the user is a partner (has partnerId) */
  isPartner: boolean;
  /** True if the user is a customer (no partnerId) */
  isCustomer: boolean;
  /** Post-login redirect path resolved during the OAuth callback. */
  postLoginRedirect: string | null;
  /** Error message from the most recent auth attempt, if any. */
  authError: string | null;
  loginWithKeycloak: (returnTo?: string) => Promise<void>;
  logout: () => Promise<void>;
  switchTenant: (tenantId: string) => void;
  /** Force-refresh the access token to pick up Keycloak role/group changes. */
  refreshRoles: () => Promise<void>;
}

/**
 * Exported solely so tests can mount `<AuthContext.Provider value={…}>` and
 * inject a deterministic auth state (user, tenantId, roles) without spinning
 * up the full Keycloak `<AuthProvider>` and its session/refresh effects.
 * Production code MUST keep using `useAuth()` / `<AuthProvider>` — never read
 * or wrap this context directly outside of test utilities.
 */
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const {
    user,
    accessToken,
    isLoading,
    isLoggingOut,
    tokenStatus,
    selectedTenantId,
    availableOrganizations,
    postLoginRedirect,
    authError,
    tenantId,
    loginWithKeycloak,
    logout,
    switchTenant,
    refreshRoles,
  } = useAuthSession();
  const navigate = useNavigate();

  const switchTenantAndNavigate = useCallback(
    (tenantId: string) => {
      if (tenantId === selectedTenantId) {
        return;
      }

      switchTenant(tenantId);
      // `navigate` returns a promise in React Router v7; nothing awaits the
      // transition here, so mark it explicitly rather than leaving it floating.
      void navigate(paths.dashboard, { replace: true });
    },
    [navigate, selectedTenantId, switchTenant],
  );

  const isAuthenticated = !!user && !!accessToken;
  const isPartner = !!user?.partnerId;
  const isCustomer = !!user && !user.partnerId;

  // Keep the module-level request context store in sync so that Orval-generated
  // fetch functions automatically receive tenant headers via customFetch.
  //
  // useLayoutEffect (not useEffect) is intentional: layout effects fire
  // synchronously before passive effects, guaranteeing the module store has
  // the correct tenant before TanStack Query's useEffect schedules the first
  // queryFn. Without this, the query fires before setRequestContext runs, and
  // the x-tenant-id header is missing → schema-registry returns 400.
  useLayoutEffect(() => {
    // `securityContext: "default"` is the canonical platform "system" value
    // used by ReactionUtils, MaterializationUtils, CSFunction_ReactionRunProcessor,
    // CentricRequests.DEFAULT_SECURITY_CONTEXT, and what the flavour-loader
    // writes records with. Reading callers MUST send the same value or the BE
    // visibility index silently hides rows — sending the tenantId (as we did
    // historically) returns an empty list for any loader-seeded data.
    setRequestContext(
      user ? { tenantId, userProfileId: user.id, securityContext: "default" } : undefined,
    );

    return () => setRequestContext(undefined);
    // Depend on stable primitives (user.id, tenantId) rather than the full user
    // object. refreshSession() creates a new user reference every 60 s even when
    // the id/tenant haven't changed; depending on the object would re-run this
    // effect (clearing then re-setting the module store) on every interval tick.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, tenantId]); // react-doctor-disable-line react-doctor/exhaustive-deps -- depend on stable user.id, not the full user object (refreshSession creates a new ref each tick)

  const contextValue = useMemo<AuthContextType>(
    () => ({
      user,
      accessToken,
      isAuthenticated,
      isLoading,
      isLoggingOut,
      tenantId,
      tokenStatus,
      selectedTenantId,
      availableOrganizations,
      isPartner,
      isCustomer,
      postLoginRedirect,
      authError,
      loginWithKeycloak,
      logout,
      switchTenant: switchTenantAndNavigate,
      refreshRoles,
    }),
    [
      accessToken,
      authError,
      availableOrganizations,
      isAuthenticated,
      isCustomer,
      isLoading,
      isLoggingOut,
      isPartner,
      postLoginRedirect,
      selectedTenantId,
      tenantId,
      tokenStatus,
      user,
      loginWithKeycloak,
      logout,
      switchTenantAndNavigate,
      refreshRoles,
    ],
  );

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = use(AuthContext);

  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};
